import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import logo from '../../images/logo/logo.jpeg';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [servicesTimeout, setServicesTimeout] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleServicesMouseEnter = () => {
    if (servicesTimeout) {
      clearTimeout(servicesTimeout);
      setServicesTimeout(null);
    }
    setIsServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsServicesOpen(false);
    }, 200); // 200ms delay before closing
    setServicesTimeout(timeout);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'Careers', path: '/careers' },
    { name: 'Our Clients', path: '/our-clients' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceItems = [
    { name: 'All Services', path: '/services' },
    { name: 'Safety Valve & Breather Valves', path: '/services/safety-valve' },
    { name: 'Actuator and positioners services', path: '/services/actuator' },
    { name: 'Valve Service', path: '/services/valve-service' },
    { name: 'ARC Valve Service', path: '/services/arc-valve' },
    { name: 'Pump Services', path: '/services/bfp-service' },
    { name: 'Spares Supply', path: '/services/spares' },
    { name: 'Workshop Support', path: '/services/workshop' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="navbar-top">
        <div className="container-custom px-4">
          <div className="navbar-top-content">
            <div className="navbar-contact-info">
              <div className="navbar-contact-item">
                <Phone className="w-4 h-4" />
                <a href="tel:+918329392431" className="text-white hover:underline">
                  +91 8329392431
                </a>
              </div>
            </div>
            <div className="navbar-support-text">
              <span>24/7 Support Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`navbar-main ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="container-custom px-4">
          <div className="navbar-content">
            {/* Logo */}
            <Link to="/" className="navbar-logo">
              <div className="navbar-logo-icon">
                <img src={logo} alt="KirBOO Flowsafe Logo" className="navbar-logo-img" />
              </div>
              <div>
                <h1 className="navbar-brand">KirBOO</h1>
                <p className="navbar-subtitle">Flowsafe Pvt Ltd</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="navbar-desktop-menu">
              {navItems.map((item) => (
                item.hasDropdown ? (
                  <div 
                    key={item.name}
                    className="navbar-services-dropdown"
                    onMouseEnter={handleServicesMouseEnter}
                    onMouseLeave={handleServicesMouseLeave}
                  >
                    <Link
                      to={item.path}
                      className={`navbar-link ${
                        location.pathname === item.path || 
                        location.pathname.startsWith('/services/')
                          ? 'navbar-link-active'
                          : 'navbar-link-inactive'
                      }`}
                    >
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </Link>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="navbar-dropdown-menu"
                      >
                        {serviceItems.map((serviceItem) => (
                          <Link
                            key={serviceItem.name}
                            to={serviceItem.path}
                            className="navbar-dropdown-item"
                            onClick={() => setIsServicesOpen(false)}
                          >
                            {serviceItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`navbar-link ${
                      location.pathname === item.path
                        ? 'navbar-link-active'
                        : 'navbar-link-inactive'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}


            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="navbar-mobile-button"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

                    {/* Mobile Navigation */}
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="navbar-mobile-menu"
              >
                <div className="navbar-mobile-content">
                  {navItems.map((item) => (
                    item.hasDropdown ? (
                      <div key={item.name} className="navbar-mobile-services">
                        <button
                          onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                          className={`navbar-mobile-link flex items-center justify-between w-full ${
                            location.pathname === item.path || 
                            location.pathname.startsWith('/services/')
                              ? 'navbar-link-active'
                              : 'navbar-link-inactive'
                          }`}
                        >
                          <span>{item.name}</span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isMobileServicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="navbar-mobile-services-list"
                          >
                            {serviceItems.map((serviceItem) => (
                              <Link
                                key={serviceItem.name}
                                to={serviceItem.path}
                                onClick={() => {
                                  setIsOpen(false);
                                  setIsMobileServicesOpen(false);
                                }}
                                className="navbar-mobile-service-link"
                              >
                                {serviceItem.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`navbar-mobile-link ${
                          location.pathname === item.path
                            ? 'navbar-link-active'
                            : 'navbar-link-inactive'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                </div>
              </motion.div>
            )}
      </nav>

      {/* Spacer for fixed navbar */}
      <div className="navbar-spacer"></div>
    </>
  );
};

export default Navbar;

